import type { ReactNode } from 'react'
import { Link } from './nav'
import { AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react'
import StatusBadge from './StatusBadge'
import BackLink, { type BackTarget } from './BackLink'
import TargetUrlBadge from './TargetUrlBadge'
import './TaskDetailLayout.css'

export type { BackTarget }

export interface TaskDetailLayoutProps {
  /** The parent surface to return to. Omit on a page reached from nowhere in particular. */
  back?: BackTarget
  /** Leading header icon. */
  icon?: ReactNode
  title: ReactNode
  /** Status string rendered as a StatusBadge after the title. */
  status?: string
  /** Extra header content after the status badge (e.g. version / judge badges). */
  headerExtra?: ReactNode
  /** The deployed app URL this task was QA'd against. Rendered as a link beneath the header. */
  targetUrl?: string | null
  /** Meta block content — compose with <MetaItem>. Omit for pages without a meta row. */
  meta?: ReactNode
  /** Footer action bar content (e.g. retrigger + guidance-update buttons). */
  footer?: ReactNode
  children: ReactNode
  /** Page-specific root class kept alongside `task-detail` so existing page CSS still applies. */
  className?: string
  testId?: string
}

/**
 * Shared backbone for every task-detail page (test run, exploration, polish pass, guidance
 * update / gap). It owns the consistent chrome — the back link, header (icon + title + status),
 * an optional meta row, and a footer action bar — while each page supplies its own body
 * sections as children. The page's original root class is preserved via `className` so its
 * existing section styles keep working unchanged.
 */
export default function TaskDetailLayout({
  back,
  icon,
  title,
  status,
  headerExtra,
  targetUrl,
  meta,
  footer,
  children,
  className,
  testId,
}: TaskDetailLayoutProps) {
  return (
    <div className={`task-detail${className ? ` ${className}` : ''}`} data-testid={testId}>
      {back && <BackLink target={back} />}
      <header className="task-detail__header">
        {icon && <span className="task-detail__icon">{icon}</span>}
        <h1 className="task-detail__title">{title}</h1>
        {status && <StatusBadge status={status} />}
        {headerExtra}
      </header>
      {targetUrl && <TargetUrlBadge url={targetUrl} testId={testId ? `${testId}-target-url` : undefined} />}
      {meta && <div className="task-detail__meta">{meta}</div>}
      {children}
      {footer && <div className="task-detail__footer">{footer}</div>}
    </div>
  )
}

/** A single label/value pair for the shared meta row. */
export function MetaItem({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="task-detail__meta-item">
      <span className="task-detail__meta-label">{label}</span>
      <span className="task-detail__meta-value">{children}</span>
    </div>
  )
}

/** Consistent loading skeleton for any task-detail page. */
export function TaskDetailSkeleton({ label, testId }: { label: string; testId?: string }) {
  return (
    <div className="task-detail" role="status" aria-live="polite" aria-label={label} data-testid={testId}>
      <div className="task-detail__skeleton-back" />
      <div className="task-detail__header">
        <div className="task-detail__skeleton-title" />
        <div className="task-detail__skeleton-badge" />
      </div>
      <div className="task-detail__skeleton-row" />
      <div className="task-detail__skeleton-row" />
      <div className="task-detail__skeleton-row task-detail__skeleton-row--short" />
    </div>
  )
}

/** Consistent error state with a retry button. */
export function TaskDetailError({
  message,
  onRetry,
  testId,
  retryTestId,
}: {
  message: string
  onRetry: () => void
  testId?: string
  retryTestId?: string
}) {
  return (
    <div className="task-detail__state task-detail__state--error" role="alert" data-testid={testId}>
      <AlertCircle size={20} aria-hidden="true" />
      <p className="task-detail__state-message">{message}</p>
      <button className="task-detail__state-btn" onClick={onRetry} data-testid={retryTestId} data-analytics-event="task-retry-button">
        <RefreshCw size={14} aria-hidden="true" /> Retry
      </button>
    </div>
  )
}

/** Consistent not-found state with a back link. */
export function TaskDetailNotFound({
  message,
  backTo,
  backLabel,
  testId,
  backTestId,
}: {
  message: string
  backTo: string
  backLabel: string
  testId?: string
  backTestId?: string
}) {
  return (
    <div className="task-detail__state" data-testid={testId}>
      <p className="task-detail__state-message">{message}</p>
      <Link to={backTo} className="task-detail__state-back" data-testid={backTestId} data-analytics-event="task-back">
        <ArrowLeft size={16} aria-hidden="true" /> {backLabel}
      </Link>
    </div>
  )
}
